<script setup>
import { computed, reactive, ref, watch } from "vue";
import { ElMessage } from "element-plus";
import { storeToRefs } from "pinia";
import { useLayoutStore } from "@/stores/layout";
import { exchangeCash } from "@/api/teachinfo";
import { getLastFlowerAmount } from "@/api/user";

const layoutStore = useLayoutStore();
const { cashDialog } = storeToRefs(layoutStore);

const visible = computed({
  get: () => !!cashDialog.value.dialog,
  set: (v) => {
    if (!v) layoutStore.closeCashDialog();
    else layoutStore.openCashDialog();
  },
});

const formRef = ref(null);
const submitting = ref(false);
const flowerAmount = ref(0);
const flowersCount = ref(0);
const remark = ref("");
const countErrors = ref("");

const formModel = reactive({
  remark: "",
});

const rules = {
  remark: [
    { required: true, message: "Required.", trigger: "blur" },
    {
      validator: (_r, v, cb) => {
        if (v && v.length > 200) cb(new Error("Remark must be less than 200 characters"));
        else cb();
      },
      trigger: "blur",
    },
  ],
};

watch(flowersCount, (data) => {
  if (data === "" || data == null) {
    countErrors.value = "Required!";
  } else if (Number(data) > flowerAmount.value) {
    countErrors.value = "Exceeding the quota, please re-enter!";
  } else {
    countErrors.value = "";
  }
});

watch(visible, (open) => {
  if (!open) return;
  flowersCount.value = 0;
  remark.value = "";
  formModel.remark = "";
  countErrors.value = "";
  formRef.value?.resetFields?.();
  getLastFlowerAmount()
    .then((res) => {
      const body = res?.data?.Body;
      flowerAmount.value = typeof body === "number" ? body : Number(body) || 0;
    })
    .catch(() => {
      flowerAmount.value = 0;
    });
});

function reduce() {
  if (flowersCount.value > 0) flowersCount.value -= 1;
}

function add() {
  if (flowersCount.value < flowerAmount.value) flowersCount.value += 1;
}

async function submit() {
  try {
    await formRef.value?.validate?.();
  } catch {
    return;
  }
  if (countErrors.value) return;
  if (!flowersCount.value) {
    ElMessage.warning("Please re-enter the valid number of flowers");
    return;
  }
  submitting.value = true;
  try {
    const res = await exchangeCash(flowersCount.value, remark.value);
    const ok = res.data?.ErrorCode === 0;
    ElMessage({
      message: res.data?.Msg || (ok ? "OK" : "Failed"),
      type: ok ? "success" : "error",
    });
    if (ok) layoutStore.closeCashDialog();
  } catch {
    ElMessage.error("Failed");
  } finally {
    submitting.value = false;
  }
}

watch(remark, (v) => {
  formModel.remark = v;
});
</script>

<template>
  <el-dialog
    v-model="visible"
    title="Convert for Cash"
    width="600px"
    class="convert-cash-dialog"
    destroy-on-close
  >
    <div class="quota-block">
      <p class="quota-line">
        <span class="quota-num">{{ flowerAmount }}</span>
        Remaining Flower Converted Quota
      </p>
      <p class="flower-tips">
        1 Flower = 5 PHP, only 100 flowers can be exchanged per month.
      </p>
    </div>

    <el-form ref="formRef" :model="formModel" :rules="rules" label-position="top">
      <div class="count-row">
        <ul class="btn-numbox">
          <li>
            <ul class="count">
              <li @click="reduce"><span class="num-jian">-</span></li>
              <li>
                <input
                  v-model.number="flowersCount"
                  type="text"
                  class="input-num"
                />
              </li>
              <li @click="add"><span class="num-jia">+</span></li>
            </ul>
          </li>
          <li><span class="kucun">NO. of Flower(s)</span></li>
        </ul>
        <span v-if="countErrors" class="count-error">{{ countErrors }}</span>
      </div>

      <el-form-item label="Remark" prop="remark">
        <el-input
          v-model="remark"
          type="textarea"
          :rows="4"
          :maxlength="200"
          show-word-limit
          resize="none"
          @input="formModel.remark = remark"
        />
      </el-form-item>
    </el-form>

    <template #footer>
      <div class="dialog-footer">
        <button
          type="button"
          class="tutor-red-btn"
          :disabled="submitting"
          @click="submit"
        >
          Transfer cash
        </button>
        <button type="button" class="tutor-cancel-btn" @click="visible = false">
          Cancel
        </button>
      </div>
    </template>
  </el-dialog>
</template>

<style scoped lang="scss">
.quota-block {
  margin-bottom: 12px;
}
.quota-line {
  margin: 0;
  font-weight: 700;
  color: $blue-title-2;
}
.quota-num {
  font-size: 24px;
  margin-right: 6px;
}
.flower-tips {
  margin: 4px 0 0;
  color: rgba(50, 57, 122, 0.4);
}
[data-theme="theme1"] .flower-tips {
  color: #ececec;
}

.count-row {
  margin-bottom: 16px;
}
.count-error {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #ee344c;
}

ul,
li {
  list-style: none;
  margin: 0;
  padding: 0;
}
.btn-numbox {
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 8px;
}
.btn-numbox .count {
  display: flex;
  overflow: hidden;
}
.btn-numbox .count .num-jian,
.input-num,
.num-jia {
  display: inline-block;
  width: 28px;
  height: 28px;
  line-height: 28px;
  text-align: center;
  font-size: 18px;
  color: #32397a;
  cursor: pointer;
  border: 1px solid #a9aeca;
  box-sizing: border-box;
}
.num-jia {
  border-radius: 0 4px 4px 0;
  background: rgba(233, 244, 255, 0.3);
}
.num-jian {
  border-radius: 4px 0 0 4px;
  background: rgba(233, 244, 255, 0.3);
}
.btn-numbox .count .input-num {
  width: 58px;
  border-left: 0;
  border-right: 0;
  cursor: text;
  font-size: 14px;
  color: var(--app-text, $blue-title);
}
.kucun {
  font-size: 12px;
  color: var(--app-text, $blue-title);
}

.dialog-footer {
  display: flex;
  justify-content: center;
  gap: 12px;
}
.tutor-red-btn {
  border: none;
  background: #ee344c;
  color: #fff;
  height: 36px;
  padding: 0 20px;
  border-radius: 4px;
  cursor: pointer;
  font-family: inherit;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}
.tutor-cancel-btn {
  border: 1px solid #a9aeca;
  background: #fff;
  color: $blue-title;
  height: 36px;
  padding: 0 20px;
  border-radius: 4px;
  cursor: pointer;
  font-family: inherit;
}
</style>
